import { useEffect, useState } from "react";
import { medicineService } from "../../../services";
import { useApi, usePagination, useDebounce } from "../../../hooks";
import { LoadingSpinner, ErrorMessage, EmptyState } from "../../common";
import { MedicineCard } from "./MedicineCard";
import { useCart } from "../../.././context/CartContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSearch, FaFilter, FaTimes, FaSort, FaTh, FaList } from "react-icons/fa";
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const PAGE_SIZE = 20;

export default function MedicineAll() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState("");
  const [selectedAlpha, setSelectedAlpha] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const { addToCart, fetchCart, isAuthenticated } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const debouncedSearch = useDebounce(searchQuery, 500);
  const pagination = usePagination(1, PAGE_SIZE);
  const { data, loading, error, execute } = useApi(medicineService.getMedicines);

  useEffect(() => {
    execute({
      page: pagination.page,
      limit: PAGE_SIZE,
      search: debouncedSearch
    });
  }, [pagination.page, debouncedSearch]);

  // Fetch cart when component mounts or when user navigates to this page
  useEffect(() => {
    if (isAuthenticated()) {
      fetchCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.key]); // Fetch on route change and navigation

  useEffect(() => {
    if (data?.pagination) {
      pagination.updatePagination(data.pagination);
    }
  }, [data]);

  const medicines = data?.data || [];

  // Client-side filtering and sorting
  const getFilteredMedicines = () => {
    let result = [...medicines];

    if (selectedAlpha) {
      result = result.filter(
        (med) =>
          med && med.name && med.name.trim().charAt(0).toLowerCase() ===
          selectedAlpha.toLowerCase()
      );
    }

    if (sort === "price-asc") {
      result.sort((a, b) => parseFloat(a.offer_price) - parseFloat(b.offer_price));
    } else if (sort === "price-desc") {
      result.sort((a, b) => parseFloat(b.offer_price) - parseFloat(a.offer_price));
    } else if (sort === "name-asc") {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === "name-desc") {
      result.sort((a, b) => b.name.localeCompare(a.name));
    }

    return result;
  };

  const filtered = getFilteredMedicines();
  const paginated = filtered; // Since API handles pagination, we display all filtered results

  const clearAllFilters = () => {
    setSearchQuery("");
    setSort("");
    setSelectedAlpha("");
    setShowMobileFilters(false);
  };

  const applyFilters = () => {
    setShowMobileFilters(false);
  };

  const MobileFilterModal = () => (
    <div style={mobileFilterOverlayStyle}>
      <div style={mobileFilterModalStyle}>
        <div style={mobileFilterHeaderStyle}>
          <h3 style={{ margin: 0, color: '#104a5f', fontSize: '18px' }}>Filters</h3>
          <button
            onClick={() => setShowMobileFilters(false)}
            style={closeButtonStyle}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Alphabet Filter */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#104a5f', marginBottom: '12px', fontSize: '16px' }}>Filter by Letter</h4>
            <div style={alphabetGridStyle}>
              {alphabet.map((letter) => (
                <button
                  key={letter}
                  onClick={() => setSelectedAlpha(letter)}
                  style={{
                    ...alphabetButtonStyle,
                    background: selectedAlpha === letter ? "#104a5f" : "#f8f9fa",
                    color: selectedAlpha === letter ? "#fff" : "#333",
                    transform: selectedAlpha === letter ? "scale(1.1)" : "scale(1)"
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
            <button
              onClick={() => setSelectedAlpha("")}
              style={clearLetterButtonStyle}
            >
              Clear Letter Filter
            </button>
          </div>

          {/* Sort */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#104a5f', marginBottom: '12px', fontSize: '16px' }}>Sort By</h4>
            <select
              onChange={(e) => setSort(e.target.value)}
              value={sort}
              style={mobileSelectStyle}
            >
              <option value="">Default Order</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A-Z</option>
              <option value="name-desc">Name: Z-A</option>
            </select>
          </div>
        </div>

        <div style={mobileFilterFooterStyle}>
          <button onClick={clearAllFilters} style={clearAllButtonStyle}>
            Clear All
          </button>
          <button onClick={applyFilters} style={applyButtonStyle}>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && !data) {
    return (
      <>
        <Header />
        <LoadingSpinner fullScreen message="Loading medicines..." />
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div style={containerStyle}>
          <ErrorMessage message={error} />
        </div>
        <Footer />
      </>
    );
  }

  // Remove full-page empty state - we'll show it inline instead

  return (
    <>
      <Header />
      <div style={containerStyle}>
        {/* Search Header */}
        <div style={searchContainerStyle}>
          <div style={searchInputWrapperStyle}>
            <FaSearch style={searchIconStyle} />
            <input
              type="text"
              placeholder="Search medicines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={searchInputStyle}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={clearSearchButtonStyle}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Mobile Controls */}
        <div style={mobileControlsStyle}>
          <button
            onClick={() => setShowMobileFilters(true)}
            style={mobileFilterButtonStyle}
          >
            <FaFilter style={{ marginRight: '8px' }} />
            Filters
            {(selectedAlpha || sort) && (
              <span style={filterBadgeStyle}>
                {(selectedAlpha ? 1 : 0) + (sort ? 1 : 0)}
              </span>
            )}
          </button>

          <div style={viewControlsStyle}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                ...viewButtonStyle,
                background: viewMode === 'grid' ? '#104a5f' : '#f8f9fa',
                color: viewMode === 'grid' ? '#fff' : '#666'
              }}
            >
              <FaTh />
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                ...viewButtonStyle,
                background: viewMode === 'list' ? '#104a5f' : '#f8f9fa',
                color: viewMode === 'list' ? '#fff' : '#666'
              }}
            >
              <FaList />
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div style={resultsSummaryStyle}>
          <span style={{ color: '#666', fontSize: '14px' }}>
            Showing {paginated.length} of {filtered.length} medicines
          </span>
          {(searchQuery || selectedAlpha || sort) && (
            <button onClick={clearAllFilters} style={clearFiltersButtonStyle}>
              Clear all filters
            </button>
          )}
        </div>

        <div style={mainContentStyle}>
          {/* Desktop Sidebar */}
          <div style={desktopSidebarStyle} className="desktop-sidebar">
            {/* Alphabet Filter */}
            <div style={filterSectionStyle}>
              <h4 style={filterTitleStyle}>Filter by Letter</h4>
              <div style={alphabetGridStyle}>
                {alphabet.map((letter) => (
                  <button
                    key={letter}
                    onClick={() => setSelectedAlpha(letter)}
                    style={{
                      ...alphabetButtonStyle,
                      background: selectedAlpha === letter ? "#104a5f" : "#f8f9fa",
                      color: selectedAlpha === letter ? "#fff" : "#333",
                      transform: selectedAlpha === letter ? "scale(1.05)" : "scale(1)"
                    }}
                  >
                    {letter}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setSelectedAlpha("")}
                style={clearLetterButtonStyle}
              >
                Clear Letter Filter
              </button>
            </div>

            {/* Sort */}
            <div style={filterSectionStyle}>
              <h4 style={filterTitleStyle}>Sort By</h4>
              <select
                onChange={(e) => setSort(e.target.value)}
                value={sort}
                style={desktopSelectStyle}
              >
                <option value="">Default Order</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A-Z</option>
                <option value="name-desc">Name: Z-A</option>
              </select>
            </div>
          </div>

          {/* Medicine Grid/List */}
          <div style={medicineContentStyle}>
            <div style={viewMode === 'grid' ? medicineGridStyle : medicineListStyle}>
              {loading
                ? Array.from({ length: PAGE_SIZE }).map((_, i) => (
                    <div key={i} style={skeletonCardStyle}>
                      <Skeleton height={120} style={{ marginBottom: '8px' }} />
                      <Skeleton count={3} height={16} style={{ marginBottom: '4px' }} />
                    </div>
                  ))
                : paginated.map((med) => (
                    <div
                      key={med.id}
                      style={{
                        ...medicineCardStyle,
                        ...(viewMode === 'list' ? listCardStyle : {})
                      }}
                      onClick={() => navigate(`/medicine/${med.id}`)}
                    >
                      <MedicineCard item={med} add={() => addToCart(med)} />
                    </div>
                  ))}
            </div>

            {/* No Results */}
            {!loading && paginated.length === 0 && (
              <div style={noResultsStyle}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔍</div>
                <h2 style={{ color: '#333', marginBottom: '12px', fontSize: '24px', fontWeight: '600' }}>
                  No medicines found
                </h2>
                <p style={{ color: '#666', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
                  {searchQuery
                    ? `We couldn't find any medicines matching "${searchQuery}"`
                    : selectedAlpha
                      ? `No medicines found starting with "${selectedAlpha}"`
                      : 'Try adjusting your search or filters'}
                </p>

                {/* Suggestions */}
                <div style={{ marginBottom: '24px', textAlign: 'left', maxWidth: '500px', margin: '0 auto 24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#104a5f' }}>
                    Suggestions:
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, color: '#666', fontSize: '14px' }}>
                    <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#104a5f' }}>✓</span> Check the spelling
                    </li>
                    <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#104a5f' }}>✓</span> Try more general search terms
                    </li>
                    <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#104a5f' }}>✓</span> Clear filters to see all products
                    </li>
                  </ul>
                </div>

                <button onClick={clearAllFilters} style={tryAgainButtonStyle}>
                  <FaTimes style={{ marginRight: '8px' }} />
                  Clear All Filters & Show All Medicines
                </button>
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={paginationStyle}>
                <button
                  onClick={pagination.previousPage}
                  disabled={!pagination.hasPreviousPage}
                  style={{
                    ...paginationButtonStyle,
                    opacity: !pagination.hasPreviousPage ? 0.5 : 1
                  }}
                >
                  Previous
                </button>

                <div style={paginationNumbersStyle}>
                  {Array.from({ length: Math.min(5, pagination.totalPages) }).map((_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => pagination.goToPage(pageNum)}
                        style={{
                          ...pageNumberButtonStyle,
                          background: pagination.page === pageNum ? "#104a5f" : "#fff",
                          color: pagination.page === pageNum ? "#fff" : "#333"
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={pagination.nextPage}
                  disabled={!pagination.hasNextPage}
                  style={{
                    ...paginationButtonStyle,
                    opacity: !pagination.hasNextPage ? 0.5 : 1
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Filter Modal */}
        {showMobileFilters && <MobileFilterModal />}

        {/* CSS for responsive behavior */}
        <style jsx>{`
          @media (max-width: 768px) {
            .desktop-sidebar {
              display: none !important;
            }
          }
          
          @media (min-width: 769px) {
            .mobile-controls {
              display: none !important;
            }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

// Styles
const containerStyle = {
  padding: "16px",
  backgroundColor: "#f8f9fa",
  minHeight: "100vh"
};

const searchContainerStyle = {
  maxWidth: "600px",
  margin: "0 auto 20px",
  position: "relative"
};

const searchInputWrapperStyle = {
  position: "relative",
  display: "flex",
  alignItems: "center"
};

const searchIconStyle = {
  position: "absolute",
  left: "16px",
  color: "#104a5f",
  zIndex: 2
};

const searchInputStyle = {
  width: "100%",
  padding: "14px 48px 14px 48px",
  fontSize: "16px",
  borderRadius: "12px",
  border: "2px solid #e9ecef",
  background: "#fff",
  outline: "none",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
};

const clearSearchButtonStyle = {
  position: "absolute",
  right: "16px",
  background: "none",
  border: "none",
  color: "#999",
  cursor: "pointer",
  padding: "4px",
  zIndex: 2
};

const mobileControlsStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "16px",
  className: "mobile-controls"
};

const mobileFilterButtonStyle = {
  display: "flex",
  alignItems: "center",
  padding: "12px 16px",
  background: "#104a5f",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: "600",
  cursor: "pointer",
  position: "relative"
};

const filterBadgeStyle = {
  position: "absolute",
  top: "-6px",
  right: "-6px",
  background: "#ff4757",
  color: "#fff",
  borderRadius: "50%",
  width: "20px",
  height: "20px",
  fontSize: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const viewControlsStyle = {
  display: "flex",
  gap: "4px"
};

const viewButtonStyle = {
  padding: "10px",
  border: "1px solid #ddd",
  borderRadius: "6px",
  cursor: "pointer",
  transition: "all 0.3s ease"
};

const resultsSummaryStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "16px",
  padding: "0 4px"
};

const clearFiltersButtonStyle = {
  background: "none",
  border: "none",
  color: "#104a5f",
  cursor: "pointer",
  fontSize: "14px",
  textDecoration: "underline"
};

const mainContentStyle = {
  display: "flex",
  gap: "24px",
  alignItems: "flex-start"
};

const desktopSidebarStyle = {
  flex: "0 0 280px",
  background: "#fff",
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
  position: "sticky",
  top: "20px"
};

const filterSectionStyle = {
  marginBottom: "32px"
};

const filterTitleStyle = {
  color: "#104a5f",
  marginBottom: "16px",
  fontSize: "18px",
  fontWeight: "600"
};

const alphabetGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(6, 1fr)",
  gap: "8px",
  marginBottom: "16px"
};

const alphabetButtonStyle = {
  width: "40px",
  height: "40px",
  fontWeight: "600",
  borderRadius: "8px",
  border: "2px solid #e9ecef",
  cursor: "pointer",
  transition: "all 0.3s ease",
  fontSize: "14px"
};

const clearLetterButtonStyle = {
  width: "100%",
  padding: "10px",
  background: "#f8f9fa",
  border: "1px solid #dee2e6",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  color: "#495057",
  transition: "all 0.3s ease"
};

const desktopSelectStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "2px solid #e9ecef",
  background: "#fff",
  fontSize: "14px",
  cursor: "pointer"
};

const medicineContentStyle = {
  flex: 1
};

const medicineGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: "20px"
};

const medicineListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const medicineCardStyle = {
  cursor: "pointer",
  transition: "transform 0.3s ease, box-shadow 0.3s ease"
};

const listCardStyle = {
  display: "flex",
  alignItems: "center"
};

const skeletonCardStyle = {
  padding: "16px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
};

const noResultsStyle = {
  textAlign: "center",
  padding: "60px 20px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
};

const tryAgainButtonStyle = {
  padding: "14px 28px",
  background: "#104a5f",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: "600",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 12px rgba(16, 74, 95, 0.2)"
};

const paginationStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  marginTop: "32px",
  padding: "20px 0"
};

const paginationButtonStyle = {
  padding: "10px 16px",
  background: "#fff",
  border: "2px solid #e9ecef",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  transition: "all 0.3s ease"
};

const paginationNumbersStyle = {
  display: "flex",
  gap: "4px"
};

const pageNumberButtonStyle = {
  width: "40px",
  height: "40px",
  border: "2px solid #e9ecef",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  transition: "all 0.3s ease"
};

// Mobile Filter Modal Styles
const mobileFilterOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  zIndex: 1000,
  display: "flex",
  alignItems: "flex-end"
};

const mobileFilterModalStyle = {
  width: "100%",
  maxHeight: "80vh",
  backgroundColor: "#fff",
  borderTopLeftRadius: "20px",
  borderTopRightRadius: "20px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column"
};

const mobileFilterHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px",
  borderBottom: "1px solid #e9ecef",
  backgroundColor: "#f8f9fa"
};

const closeButtonStyle = {
  background: "none",
  border: "none",
  fontSize: "18px",
  cursor: "pointer",
  color: "#666",
  padding: "4px"
};

const mobileSelectStyle = {
  width: "100%",
  padding: "14px",
  borderRadius: "8px",
  border: "2px solid #e9ecef",
  background: "#fff",
  fontSize: "16px",
  cursor: "pointer"
};

const mobileFilterFooterStyle = {
  display: "flex",
  gap: "12px",
  padding: "20px",
  borderTop: "1px solid #e9ecef",
  backgroundColor: "#f8f9fa"
};

const clearAllButtonStyle = {
  flex: 1,
  padding: "14px",
  background: "#fff",
  border: "2px solid #104a5f",
  color: "#104a5f",
  borderRadius: "8px",
  fontSize: "16px",
  fontWeight: "600",
  cursor: "pointer"
};

const applyButtonStyle = {
  flex: 1,
  padding: "14px",
  background: "#104a5f",
  border: "2px solid #104a5f",
  color: "#fff",
  borderRadius: "8px",
  fontSize: "16px",
  fontWeight: "600",
  cursor: "pointer"
};